import axios from 'axios'
import { GEC_ML_END_POINT } from '@/const/chatbot'
import common from '@/utils/common'
import { isReactNativeWebView, playWithMimeFallback } from './react-native-player'

const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || /iPad|iPhone|iPod/.test(navigator.userAgent)

// When audio.play() is blocked (NotAllowedError), the browser requires a fresh
// user gesture. This injects a small floating button so the user can tap to start
// playback manually. Returns a cleanup function that removes the button.
const showManualPlayButton = onPlay => {
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.textContent = '▶  Tap to play'
  Object.assign(btn.style, {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    zIndex: '2147483647',
    padding: '12px 20px',
    background: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: '9999px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
  })
  const remove = () => { if (btn.isConnected) document.body.removeChild(btn) }
  btn.addEventListener('click', () => { remove(); onPlay() })
  document.body.appendChild(btn)
  return remove
}

// Realtime relay endpoints (WebRTC signaling), mirroring the handshake used by
// the `realtime-rea.html` test harness: a session is minted server-side (the
// OpenAI key never reaches the browser), then the browser's SDP offer is
// relayed through the same backend to establish the peer connection.
const REALTIME_SESSION_ENDPOINT = `${GEC_ML_END_POINT}/common/realtime/session`
const REALTIME_SDP_ENDPOINT = `${GEC_ML_END_POINT}/common/realtime/sdp`

// Only these voices are valid for the Realtime API. TTS-only voices (onyx,
// nova, fable, ...) are rejected by the realtime model, so fall back to a
// realtime-safe default rather than letting the handshake fail.
const REALTIME_VOICES = new Set(['alloy', 'ash', 'ballad', 'cedar', 'coral', 'echo', 'marin', 'sage', 'shimmer', 'verse'])

const supportsRealtimeAudio = () => typeof window !== 'undefined' && typeof window.RTCPeerConnection === 'function'

// The Realtime session is a conversational agent, not a plain TTS renderer,
// so it needs an explicit instruction to just read the text back verbatim
// instead of reacting to it as a user turn.
const buildSpeakInstruction = text => `You are a text-to-speech voice. Read the message below aloud exactly as written, in a warm and natural voice. Do not add, omit, rephrase, or comment on it, and do not ask any questions.\n\nMessage: "${text}"`

/**
 * Performs the WebRTC signaling handshake against the GEC ML Realtime relay:
 * mint a session (server holds the OpenAI key), negotiate a receive-only
 * audio peer connection, relay the SDP offer/answer, then seed whatever
 * bootstrap events the server prepared. Shared by every realtime helper
 * below so the handshake itself only lives in one place.
 */
const openRealtimeConnection = async payload => {
  const sessionResponse = await axios.post(REALTIME_SESSION_ENDPOINT, payload)
  const session = sessionResponse.data?.data ?? {}
  const cacheKey = session.cache_key
  if (!cacheKey) {
    throw new Error('Realtime session did not return a cache_key.')
  }

  const pc = new RTCPeerConnection()
  // Receive-only: these helpers only need the model's speech, never a mic.
  pc.addTransceiver('audio', { direction: 'recvonly' })

  const audioStream = new Audio()
  audioStream.autoplay = true
  audioStream.playsInline = true

  const remoteTrackReady = new Promise(resolve => {
    pc.ontrack = event => {
      const [remoteStream] = event.streams
      audioStream.srcObject = remoteStream
      resolve()
    }
  })

  const dc = pc.createDataChannel('oai-events', { ordered: true })
  const dcOpen = new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Timed out opening the realtime data channel.')), 15000)
    dc.addEventListener('open', () => {
      clearTimeout(timer)
      resolve()
    }, { once: true })
  })

  const offer = await pc.createOffer()
  await pc.setLocalDescription(offer)
  const sdpUrl = new URL(REALTIME_SDP_ENDPOINT)
  if (sdpUrl.protocol === 'http:') sdpUrl.protocol = 'https:'
  sdpUrl.searchParams.set('key', cacheKey)
  if (payload.userId) sdpUrl.searchParams.set('userId', payload.userId)

  const sdpResponse = await fetch(sdpUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/sdp' },
    body: offer.sdp,
    credentials: 'omit',
  })
  const answer = await sdpResponse.text()
  if (!sdpResponse.ok) {
    throw new Error(`Realtime SDP relay failed (${sdpResponse.status}): ${answer.slice(0, 300)}`)
  }

  await pc.setRemoteDescription({ type: 'answer', sdp: answer })
  await dcOpen

  // Seed whatever bootstrap events the server prepared (e.g. the initial
  // response.create) so the model starts speaking without further input.
  const bootstrapEvents = session.bootstrap || []
  bootstrapEvents.forEach(event => {
    if (dc.readyState === 'open') dc.send(JSON.stringify(event))
  })

  await remoteTrackReady

  return {
    pc,
    dc,
    audioStream,
    cacheKey,
  }
}

/**
 * Builds the session payload for having the model speak `text` aloud
 * verbatim (used by `streamAudio`). No microphone is captured for this
 * flow - the peer connection only negotiates a receive-only audio
 * transceiver, so playback starts as soon as the model's first RTP audio
 * packets arrive instead of waiting for a full audio file to be generated
 * and downloaded.
 */
const buildSpeakSessionPayload = (text, { voice = 'shimmer', feature = null } = {}) => ({
  prompt: buildSpeakInstruction(text),
  input: null,
  main_points: [],
  language: null,
  voice: REALTIME_VOICES.has(voice) ? voice : 'shimmer',
  history: [],
  output: 'audio',
  modeId: null,
  url: location.href,
  feature,
  userId: common.AUTH_USER()?.id ?? null,
  // No mic input, so no need for voice-activity-detection turn taking.
  auto_listen: false,
  silence_ms: 600,
  // Have the model speak first even though there is no user `input`.
  greet: true,
})

// Data-channel event types that mean "the model has started speaking" - over
// WebRTC the audio itself rides the RTP track, so these just announce it.
const REALTIME_AUDIO_START_EVENTS = new Set([
  'output_audio_buffer.started',
  'response.audio.delta',
  'response.output_audio.delta',
])

/**
 * Opens a realtime connection for one conversational turn - the server
 * already seeds a `conversation.item.create` + `response.create` from the
 * session's `input`, so the model starts speaking as soon as the data
 * channel opens. Tracks the spoken transcript and resolves the same
 * `{ success, error, cacheKey, assistantMessage }` shape the REST-based
 * `streamAudioFromGecMl`/`streamBaseAudio` callers already expect from
 * `onComplete`, so it's a drop-in transport swap for them.
 */
const runRealtimeTurn = async (payload, onPlaying = null, onComplete = null) => {
  const {
    pc, dc, audioStream, cacheKey,
  } = await openRealtimeConnection(payload)

  let hasStartedPlaying = false
  let transcript = ''
  let closed = false
  let finalized = false
  let responseDone = false
  let audioBufferStopped = false
  let fallbackTimer = null

  const closeConnection = () => {
    if (closed) return
    closed = true
    clearTimeout(fallbackTimer)
    try { dc.close() } catch { /* noop */ }
    try { pc.close() } catch { /* noop */ }
    audioStream.pause()
    audioStream.srcObject = null
  }

  // `response.done` only means the model finished *generating* the reply -
  // the audio itself keeps streaming over the RTP track afterwards, so
  // closing the connection right on `response.done` cuts playback off after
  // only the first few words. `output_audio_buffer.stopped` is the signal
  // that the server has actually finished streaming this response's audio,
  // so wait for both before tearing the connection down.
  const finish = (success, error = null) => {
    if (finalized) return
    finalized = true
    closeConnection()
    if (typeof onComplete === 'function') {
      onComplete({
        success,
        error,
        cacheKey,
        assistantMessage: transcript,
      })
    }
  }

  dc.addEventListener('message', event => {
    let realtimeEvent
    try {
      realtimeEvent = JSON.parse(event.data)
    } catch {
      return
    }

    if (REALTIME_AUDIO_START_EVENTS.has(realtimeEvent.type) && !hasStartedPlaying) {
      hasStartedPlaying = true
      audioStream.play().catch(error => console.error('Error playing realtime audio:', error))
      if (typeof onPlaying === 'function') onPlaying()
      return
    }

    switch (realtimeEvent.type) {
      case 'response.audio_transcript.delta':
      case 'response.output_audio_transcript.delta':
        transcript += realtimeEvent.delta || ''
        break
      case 'response.audio_transcript.done':
      case 'response.output_audio_transcript.done':
        transcript = realtimeEvent.transcript || transcript
        break
      case 'output_audio_buffer.stopped':
        audioBufferStopped = true
        if (responseDone) finish(true)
        break
      case 'response.done':
        responseDone = true
        if (audioBufferStopped) {
          finish(true)
        } else {
          // Safety net: fall back to closing a few seconds after generation
          // finishes in case the server never emits
          // `output_audio_buffer.stopped` for some reason, so the
          // connection doesn't get left open indefinitely.
          clearTimeout(fallbackTimer)
          fallbackTimer = setTimeout(() => finish(true), 8000)
        }
        break
      case 'error':
        finish(false, realtimeEvent.error)
        break
      default:
        break
    }
  })

  return {
    audioStream,
    stop() {
      finalized = true
      closeConnection()
    },
  }
}

/**
 * Streams realtime, low-latency speech playback of `text` over a WebRTC
 * session against the GEC ML Realtime relay (see `runRealtimeTurn`).
 * Falls back to the legacy HTTP audio-file endpoint when WebRTC isn't
 * available (e.g. inside the React Native WebView, or on very old browsers).
 */
const streamAudio = (text, endpoint = '/api/tts-ai-version', onPlaying = null, onComplete = null, options = {}) => {
  if (isReactNativeWebView()) {
    const url = `${endpoint}?text=${text}`
    let inner = null
    let stopped = false
    playWithMimeFallback(url, {
      onPlaying,
      onComplete,
      completePayload: true,
      revokeOnEnd: true,
    })
      .then(stream => {
        if (stopped) {
          stream.stop()
          return
        }
        inner = stream
      })
      .catch(error => {
        console.error('Error playing audio in RN WebView:', error)
      })
    return {
      audioStream: null,
      stop() {
        stopped = true
        if (inner) inner.stop()
      },
    }
  }

  if (!supportsRealtimeAudio()) {
    // No WebRTC support: fall back to the plain HTTP audio-file endpoint.
    const url = `${endpoint}?text=${text}`
    const audioStream = new Audio(url)
    audioStream
      .play()
      .then(() => {
        if (typeof onPlaying === 'function') onPlaying()
      })
      .catch(error => console.error('Error playing audio:', error))
    audioStream.addEventListener('ended', () => {
      if (typeof onComplete === 'function') onComplete(true)
    })
    return {
      audioStream,
      stop() {
        audioStream.pause()
        audioStream.currentTime = 0
      },
    }
  }

  let controller = null
  let stopped = false

  runRealtimeTurn(buildSpeakSessionPayload(text, options), onPlaying, result => {
    if (typeof onComplete === 'function') onComplete(result.success)
  })
    .then(activeController => {
      if (stopped) {
        activeController.stop()
        return
      }
      controller = activeController
    })
    .catch(error => {
      console.error('Error starting realtime audio stream:', error)
      if (typeof onComplete === 'function') onComplete(false)
    })

  return {
    audioStream: null,
    stop() {
      stopped = true
      if (controller) controller.stop()
    },
  }
}

const streamText = async (params, onStream) => {
  // eslint-disable-next-line no-param-reassign
  params.output = 'text'
  if (common.AUTH_USER()?.id) {
    // eslint-disable-next-line no-param-reassign
    params.userId = common.AUTH_USER()?.id
  }
  try {
    const response = await fetch(`${GEC_ML_END_POINT}/common/tts/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    })

    if (!response.body) {
      throw new Error('ReadableStream not supported or empty response.')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()

    let streamedText = ''
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read()
      if (done) break

      const textChunk = decoder.decode(value, { stream: true })
      streamedText += textChunk
      onStream(textChunk)
    }

    return {
      success: true,
      text: streamedText,
    }
  } catch (error) {
    return {
      success: false,
      error,
    }
  }
}

/**
 * Legacy REST/audio-file transport for `streamAudioFromGecMl`: renders the
 * turn via `POST /common/tts/stream`, then plays the resulting audio file
 * (or its cached blob on Safari / inside the RN WebView). Kept as the
 * fallback path when WebRTC isn't available, or when the realtime handshake
 * fails for any reason.
 */
const streamAudioFromGecMlRest = async ({
  text, mainPoint, language, voice, history, output = 'audio', modeId = null, feature = null,
}, onPlaying = null, onComplete = null) => {
  try {
    const endpoint = `${GEC_ML_END_POINT}/common/tts/stream`
    const getCacheKey = await axios.post(endpoint, {
      prompt: '-',
      input: text,
      main_points: mainPoint,
      language,
      voice,
      history,
      output,
      modeId,
      url: location.href,
      feature,
      userId: common.AUTH_USER()?.id ?? null,
      ...(isSafari() && { stream: false }),
    })
    const cacheKey = getCacheKey.data.data.cache_key
    const streamUrl = `${endpoint}?prompt=${cacheKey}`
    console.log(streamUrl)
    if (isReactNativeWebView()) {
      return await playWithMimeFallback(streamUrl, {
        onPlaying,
        onComplete,
        completePayload: { success: true, error: null, cacheKey },
        errorPayload: { cacheKey },
        revokeOnEnd: true,
      })
    }
    if (isSafari()) {
      const audioResponse = await fetch(streamUrl)
      if (!audioResponse.ok) throw new Error('Audio fetch failed')
      const blob = await audioResponse.blob()
      const blobUrl = URL.createObjectURL(blob)
      console.log(blobUrl)
      const audioStream = new Audio(blobUrl)
      let removePlayButton = null

      const doPlay = () => {
        audioStream
          .play()
          .then(() => {
            if (typeof onPlaying === 'function') onPlaying()
          })
          .catch(error => {
            if (error.name === 'NotAllowedError' && !removePlayButton) {
              removePlayButton = showManualPlayButton(() => {
                removePlayButton = null
                doPlay()
              })
            } else if (error.name !== 'NotAllowedError') {
              URL.revokeObjectURL(blobUrl)
              if (typeof onComplete === 'function') onComplete({ success: false, error, cacheKey })
            }
          })
      }

      audioStream.addEventListener('ended', () => {
        URL.revokeObjectURL(blobUrl)
        if (typeof onComplete === 'function') onComplete({ success: true, error: null, cacheKey })
      })

      doPlay()

      return {
        audioStream,
        stop() {
          if (removePlayButton) { removePlayButton(); removePlayButton = null }
          audioStream.pause()
          audioStream.currentTime = 0
          URL.revokeObjectURL(blobUrl)
        },
      }
    }
    // TODO: Handle Stream Error
    const audioStream = new Audio(streamUrl)
    let removePlayButton = null

    const doPlay = () => {
      audioStream
        .play()
        .then(() => {
          if (typeof onPlaying === 'function') {
            onPlaying() // Call the provided callback when audio starts playing
          }
        })
        .catch(error => {
          if (error.name === 'NotAllowedError' && !removePlayButton) {
            removePlayButton = showManualPlayButton(() => {
              removePlayButton = null
              doPlay()
            })
          } else if (error.name !== 'NotAllowedError') {
            if (typeof onComplete === 'function') {
              onComplete({
                success: false,
                error,
                cacheKey,
              })
            }
            throw error
          }
        })
    }

    audioStream.addEventListener('ended', () => {
      if (typeof onComplete === 'function') {
        onComplete({
          success: true,
          error: null,
          cacheKey,
        }) // Call the provided callback after audio ends
      }
    })

    doPlay()

    return {
      audioStream,
      stop() {
        if (removePlayButton) { removePlayButton(); removePlayButton = null }
        audioStream.pause() // Stop the audio  playback
        audioStream.currentTime = 0 // Reset playback to the beginning
      },
    }
  } catch (error) {
    console.error(error)
    onComplete({
      success: false,
      error,
    })
    return null
  }
}

/**
 * Streams one REA conversational turn: the model answers `text` (the
 * student's message) given `mainPoint`/`language`/`voice`/`history`/`modeId`.
 * Prefers a realtime WebRTC session (see `runRealtimeTurn`) so the model's
 * reply starts playing as soon as the first audio packets arrive, instead of
 * waiting for a full audio file to be rendered server-side and downloaded.
 * Falls back to the legacy `POST /common/tts/stream` + audio-file transport
 * (`streamAudioFromGecMlRest`) inside the RN WebView, when WebRTC isn't
 * available, or if the realtime handshake fails for any reason.
 */
const streamAudioFromGecMl = async ({
  text, mainPoint, language, voice, history, output = 'audio', modeId = null, feature = null,
}, onPlaying = null, onComplete = null) => {
  if (!isReactNativeWebView() && supportsRealtimeAudio()) {
    try {
      return await runRealtimeTurn({
        prompt: '-',
        input: text,
        main_points: mainPoint,
        language,
        voice: REALTIME_VOICES.has(voice) ? voice : 'shimmer',
        history,
        output,
        modeId,
        url: location.href,
        feature,
        userId: common.AUTH_USER()?.id ?? null,
        // The seeded `input` already triggers a response, so no need for a
        // separate greet, and there's no mic input to VAD-listen for.
        auto_listen: false,
        silence_ms: 600,
        greet: false,
      }, onPlaying, onComplete)
    } catch (error) {
      console.error('Realtime REA turn failed, falling back to REST TTS stream:', error)
      // Fall through to the REST/audio-file transport below.
    }
  }

  return streamAudioFromGecMlRest({
    text, mainPoint, language, voice, history, output, modeId, feature,
  }, onPlaying, onComplete)
}

const streamBaseAudio = async ({
  input, prompt, language, voice, history = [], modelParameter = null, feature = null,
}, onPlaying = null, onComplete = null) => {
  try {
    const endpoint = `${GEC_ML_END_POINT}/common/tts/stream`
    const getCacheKey = await axios.post(endpoint, {
      prompt: prompt || '-',
      input,
      language,
      voice,
      history,
      model_parameter: modelParameter,
      url: location.href,
      feature,
      userId: common.AUTH_USER()?.id ?? null,
      ...(isSafari() && { stream: false }),
    })
    const cacheKey = getCacheKey.data.data.cache_key
    const streamUrl = `${endpoint}?prompt=${cacheKey}`
    if (isReactNativeWebView()) {
      return await playWithMimeFallback(streamUrl, {
        onPlaying,
        onComplete,
        completePayload: { success: true, error: null, cacheKey },
        errorPayload: { cacheKey },
        revokeOnEnd: true,
      })
    }
    if (isSafari()) {
      const audioResponse = await fetch(streamUrl)
      if (!audioResponse.ok) throw new Error('Audio fetch failed')
      const blob = await audioResponse.blob()
      const blobUrl = URL.createObjectURL(blob)
      const audioStream = new Audio(blobUrl)
      console.log(blobUrl)
      let removePlayButton = null

      const doPlay = () => {
        audioStream
          .play()
          .then(() => {
            if (typeof onPlaying === 'function') onPlaying()
          })
          .catch(error => {
            if (error.name === 'NotAllowedError' && !removePlayButton) {
              removePlayButton = showManualPlayButton(() => {
                removePlayButton = null
                doPlay()
              })
            } else if (error.name !== 'NotAllowedError') {
              URL.revokeObjectURL(blobUrl)
              if (typeof onComplete === 'function') onComplete({ success: false, error, cacheKey })
            }
          })
      }

      audioStream.addEventListener('ended', () => {
        URL.revokeObjectURL(blobUrl)
        if (typeof onComplete === 'function') onComplete({ success: true, error: null, cacheKey })
      })

      doPlay()

      return {
        audioStream,
        stop() {
          if (removePlayButton) { removePlayButton(); removePlayButton = null }
          audioStream.pause()
          audioStream.currentTime = 0
          URL.revokeObjectURL(blobUrl)
        },
      }
    }
    const audioStream = new Audio(streamUrl)
    let removePlayButton = null

    const doPlay = () => {
      audioStream
        .play()
        .then(() => {
          if (typeof onPlaying === 'function') {
            onPlaying() // Call the provided callback when audio starts playing
          }
        })
        .catch(error => {
          if (error.name === 'NotAllowedError' && !removePlayButton) {
            removePlayButton = showManualPlayButton(() => {
              removePlayButton = null
              doPlay()
            })
          } else if (error.name !== 'NotAllowedError') {
            throw new Error(error)
          }
        })
    }

    audioStream.addEventListener('ended', () => {
      if (typeof onComplete === 'function') {
        onComplete({
          success: true,
          error: null,
          cacheKey,
        }) // Call the provided callback after audio ends
      }
    })

    doPlay()

    return {
      audioStream,
      stop() {
        if (removePlayButton) { removePlayButton(); removePlayButton = null }
        audioStream.pause() // Stop the audio  playback
        audioStream.currentTime = 0 // Reset playback to the beginning
      },
    }
  } catch (error) {
    console.error(error)
    onComplete({
      success: false,
      error,
    })
    return null
  }
}

/**
 * Streams audio of an already generated text.
 * Unlike `streamBaseAudio`, this endpoint runs no model of its own — it only
 * voices the text it is given, so it suits replies produced by the GEC backend.
 */
const streamTextAudio = async ({ text, voice = 'shimmer' }, onPlaying = null, onComplete = null) => {
  try {
    const response = await fetch(`${GEC_ML_END_POINT}/common/base/tts/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text, voice }),
    })

    if (!response.ok) {
      throw new Error(await response.text())
    }

    const audioBlob = await response.blob()
    const audioURL = URL.createObjectURL(audioBlob)
    const audioStream = new Audio(audioURL)
    let removePlayButton = null

    const cleanUp = () => {
      URL.revokeObjectURL(audioURL)
      if (removePlayButton) {
        removePlayButton()
        removePlayButton = null
      }
    }

    const doPlay = () => {
      audioStream
        .play()
        .then(() => {
          if (typeof onPlaying === 'function') onPlaying()
        })
        .catch(error => {
          if (error.name === 'NotAllowedError' && !removePlayButton) {
            removePlayButton = showManualPlayButton(() => {
              removePlayButton = null
              doPlay()
            })
          } else if (error.name !== 'NotAllowedError') {
            cleanUp()
            if (typeof onComplete === 'function') onComplete({ success: false, error })
          }
        })
    }

    audioStream.addEventListener('ended', () => {
      cleanUp()
      if (typeof onComplete === 'function') onComplete({ success: true, error: null })
    })

    doPlay()

    return {
      audioStream,
      stop() {
        audioStream.pause()
        audioStream.currentTime = 0
        cleanUp()
      },
    }
  } catch (error) {
    console.error(error)
    if (typeof onComplete === 'function') {
      onComplete({ success: false, error })
    }
    return null
  }
}

const voiceStream = async ({
  text, voice,
}, onPlaying = null, onComplete = null, feature = null) => {
  try {
    const response = await fetch(`${GEC_ML_END_POINT}/common/text-to-audio/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text,
        voice,
        url: location.href,
        feature,
        ...(common.AUTH_USER()?.id ? { userId: common.AUTH_USER().id } : {}),
      }),
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'An error occurred. Please try again.')
    }

    const audioBlob = await response.blob()
    const audioURL = URL.createObjectURL(audioBlob)
    const audioStream = new Audio(audioURL)

    audioStream
      .play()
      .then(() => {
        if (typeof onPlaying === 'function') {
          onPlaying()
        }
      })
      .catch(error => { throw new Error(error) })

    audioStream.addEventListener('ended', () => {
      if (typeof onComplete === 'function') {
        onComplete({
          success: true,
          error: null,
        })
      }
      URL.revokeObjectURL(audioURL) // Clean up the blob URL after playback
    })

    return {
      audioStream,
      stop() {
        audioStream.pause()
        audioStream.currentTime = 0
        URL.revokeObjectURL(audioURL)
      },
    }
  } catch (error) {
    console.error(error)
    if (typeof onComplete === 'function') {
      onComplete({
        success: false,
        error,
      })
    }
    return null
  }
}

async function getStreamedText(cacheKey) {
  try {
    const text = `${cacheKey}_sentence`
    const userId = common.AUTH_USER()?.id ?? null
    const response = await fetch(`${GEC_ML_END_POINT}/common/tts/text?key=${text}&userId=${userId}&url=${location.href}`)

    if (!response.ok) {
      throw new Error(await response.text())
    }

    const localeJson = await response.json()
    return localeJson.text
  } catch (error) {
    console.error(error)
    throw error
  }
}

export {
  getStreamedText,
  streamText,
  voiceStream,
  streamAudio,
  streamBaseAudio,
  streamTextAudio,
  streamAudioFromGecMl,
}
